import {
  useGetEnrollmentMultiDocumentVerificationData,
  useGetEnrollmentMultiDocumentVerificationDataV2,
  useSubmitEnrollmentMultiDocumentVerificationData,
  useSubmitEnrollmentMultiDocumentVerificationDataV2,
  useSubmitEnrollmentMultiDocVerificationCheckStatusData,
  useSubmitEnrollmentMultiDocVerificationCheckStatusDataV2,
} from '@egw/api'
import { DocumentVerificationStatus } from '@egw/api/src/models/enrollment/enrollment'
import { Alert, AlertType, Button, GenericError, ProgressBar } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useRef, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import {
  BackId,
  FaceMatch,
  FrontId,
  ProcessId,
  RetrySteps,
  Selfie,
  ResetPermissions,
  usePermissions,
  CameraPermissionState,
  ConsentScreen,
  MobileRedirect,
} from '@egw/features-auth'
import { getIncodeClient } from '@egw/risk-service'
import cx from 'classnames'
import { passkeyService } from '@egw/auth'
import { PageHeader } from '../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Multi - DocumentVerification')

const INTERVAL_TO_POLL_SUBMIT_ENROLLMENT = 5000

export const DocumentVerificationPage: FC = () => {
  const { flags } = useFlags<FeatureFlags>()

  return flags.rolloutIncodeSdkDocumentVerificationEnrollment ? <DocumentVerificationV2 /> : <DocumentVerification />
}

export const DocumentVerification: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, isLoading, error } = useGetEnrollmentMultiDocumentVerificationData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiDocumentVerificationData()
  const { mutate: checkStatus, error: checkStatusError } = useSubmitEnrollmentMultiDocVerificationCheckStatusData()

  const [isPending, setIsPending] = useState(false)
  const [verifyDocumentUrl, setVerifyDocumentUrl] = useState<string>()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    try {
      const response = await mutate({
        triggerDocumentVerification: true,
      })

      const docStatus = response?.enrollmentData?.documentVerificationStatus
      const docUrl = response?.enrollmentData?.documentVerificationUrl

      if (docStatus === DocumentVerificationStatus.Pending) {
        setIsPending(true)
        setVerifyDocumentUrl(docUrl)
        window.open(docUrl, '_blank')
      } else {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onPollSubmit = async (): Promise<void> => {
    try {
      const response = await checkStatus({})

      if (response?.__typename === 'SubmitEnrollmentError') {
        switch (response.type) {
          case 'APPLICANT_IN_MANUAL_REVIEW':
            return void navigate(
              `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.UNDER_REVIEW.path}`,
            )
          case 'USER_UNABLE_TO_ENROLL':
            return void navigate(
              `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.DECLINE.path}`,
            )
          default:
            return void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        }
      }

      if (
        response?.__typename === 'SubmitEnrollmentSuccess' &&
        response.enrollmentData?.documentVerificationStatus === DocumentVerificationStatus.Complete
      ) {
        setIsPending(false)

        if (response.path) {
          void navigate(response.path)
        } else {
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        }
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  useEffect(() => {
    if (applicationId) {
      sendEvent('bankmulti_enroll_docV_started', {
        applicationId,
      })
    }
  }, [applicationId])

  useEffect(() => {
    if (!isPending) {
      return
    }

    intervalRef.current = setInterval(() => {
      void onPollSubmit()
    }, INTERVAL_TO_POLL_SUBMIT_ENROLLMENT)

    return () => {
      const intervalId = intervalRef.current
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isPending])

  return (
    <EnrollmentMultiLayout title={t('multi.doc-verification.page_title')}>
      <PageHeader className="mb-sm" title={t('multi.doc-verification.title')} />
      <ProgressBar
        className="mb-xl"
        progressPercentage={(11 / 13) * 100}
        previousPercentage={(10 / 13) * 100}
        showAnimation
      />
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div>
          {!isPending ? (
            <>
              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.why_required_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('multi.doc-verification.why_required_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.is_info_secure_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('multi.doc-verification.is_info_secure_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-sm">
                {t('multi.doc-verification.how_verification_work')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.photograph_id_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-sm">
                {t('multi.doc-verification.photograph_id_description')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.take_a_selfie')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('multi.doc-verification.take_a_selfie_description')}
              </p>
            </>
          ) : null}

          {isPending ? (
            <>
              <p className="typo-primary-base-regular text-text-meta mb-xl">{t('multi.doc-verification.thank_you')}</p>
              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.complete_verification')}
              </p>
              <a
                href={verifyDocumentUrl}
                target="_blank"
                className="typo-primary-base-regular text-text-action mb-xl"
                rel="noopener"
              >
                {verifyDocumentUrl}
              </a>
              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('multi.doc-verification.complete_verification_process')}
              </p>
            </>
          ) : null}

          <Alert
            alertType={AlertType.Info}
            showIcon={false}
            description={t('multi.doc-verification.complete_alert')}
            className="mb-xl"
          />

          <div className="flex-1 md:hidden" />

          {submitError ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isLoading={isSubmitting || isPending}
            onClick={() => void onSubmit()}
            testId="primaryAction_button"
          >
            {isPending
              ? t('multi.doc-verification.verification_in_progress')
              : t('multi.doc-verification.start_verification')}
          </Button>
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? checkStatusError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}

const saveDeviceData = (session: { token: string }): void => {
  void getIncodeClient().sendGeolocation({ token: session.token })
  void getIncodeClient().sendFingerprint({ token: session.token })
}

export const DocumentVerificationV2: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const isMobile = passkeyService.getIsMobile()

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, isLoading, error } = useGetEnrollmentMultiDocumentVerificationDataV2()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiDocumentVerificationDataV2()
  const { mutate: checkStatus, error: checkStatusError } = useSubmitEnrollmentMultiDocVerificationCheckStatusDataV2()

  const [isShowDocV, setIsShowDocV] = useState(false)

  const permissionsState = usePermissions()

  const [incodeSession, setIncodeSession] = useState<{ token: string } | undefined>(undefined)
  const [isIncodeError, setIsIncodeError] = useState(false)
  const [step, setStep] = useState(0)
  const [resetPermissions, setResetPermissions] = useState(false)
  const [liveness, setLiveness] = useState(false)
  const [userExists, setUserExists] = useState(false)

  const [isPending, setIsPending] = useState(false)
  const [verifyId, setVerifyId] = useState<string>()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    try {
      const response = await mutate({
        triggerDocumentVerification: true,
      })

      const docStatus = response?.enrollmentData?.documentVerificationStatus
      const documentVerificationId = response?.enrollmentData?.documentVerificationId

      if (docStatus === DocumentVerificationStatus.Pending) {
        setVerifyId(documentVerificationId)
        setIsShowDocV(true)
        setIsPending(true)
      } else {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onPollSubmit = async (): Promise<void> => {
    try {
      const response = await checkStatus({})

      if (response?.__typename === 'SubmitEnrollmentError') {
        switch (response.type) {
          case 'APPLICANT_IN_MANUAL_REVIEW':
            return void navigate(
              `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.UNDER_REVIEW.path}`,
            )
          case 'USER_UNABLE_TO_ENROLL':
            return void navigate(
              `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.DECLINE.path}`,
            )
          default:
            return void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        }
      }

      if (
        response?.__typename === 'SubmitEnrollmentSuccess' &&
        response.enrollmentData?.documentVerificationStatus === DocumentVerificationStatus.Complete
      ) {
        setIsPending(false)

        if (response.path) {
          void navigate(response.path)
        } else {
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        }
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  useEffect(() => {
    if (applicationId) {
      sendEvent('bankmulti_enroll_docV_started', {
        applicationId,
      })
    }
  }, [applicationId])

  useEffect(() => {
    if (!isPending) {
      return
    }

    intervalRef.current = setInterval(() => {
      void onPollSubmit()
    }, INTERVAL_TO_POLL_SUBMIT_ENROLLMENT)

    return () => {
      const intervalId = intervalRef.current
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isPending])

  useEffect(() => {
    if (!verifyId) {
      return
    }

    void getIncodeClient()
      .createSession('US', verifyId)
      .then(async (session) => {
        await getIncodeClient().warmup()
        setIncodeSession(session)

        saveDeviceData(session)
      })
      .catch((e) => {
        log.error('Error creating Incode session', e)
        setIncodeSession(undefined)
        setIsIncodeError(true)
      })
  }, [verifyId])

  const goNext = (): void => {
    setStep(step + 1)
  }

  useEffect(() => {
    // if permissions are denied from start, let's show the reset permissions screen
    setResetPermissions(permissionsState === CameraPermissionState.DENIED)
  }, [permissionsState])

  const handleError = (e?: { type: string }): void => {
    if (e?.type === 'permissionDenied') {
      setResetPermissions(true)
    }
  }

  if (resetPermissions) {
    return (
      <ResetPermissions
        onTryAgain={() => {
          setResetPermissions(false)
        }}
      />
    )
  }

  return (
    <EnrollmentMultiLayout
      title={t('multi.doc-verification.page_title')}
      className={cx({
        '!max-w-full': isShowDocV && incodeSession,
      })}
    >
      {!incodeSession ? (
        <>
          <PageHeader className="mb-sm" title={t('multi.doc-verification.title')} />
          <ProgressBar
            className="mb-xl"
            progressPercentage={(11 / 13) * 100}
            previousPercentage={(10 / 13) * 100}
            showAnimation
          />
        </>
      ) : null}

      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="100px" className="mb-10" />
          <Skeleton count={5} height="50px" />
        </SkeletonTheme>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          {!isShowDocV || !incodeSession ? (
            <>
              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.why_required_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('multi.doc-verification.why_required_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.is_info_secure_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('multi.doc-verification.is_info_secure_description')}
              </p>

              <p className="typo-primary-lg-semibold text-text-header mb-sm">
                {t('multi.doc-verification.how_verification_work')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.photograph_id_title')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-sm">
                {t('multi.doc-verification.photograph_id_description')}
              </p>

              <p className="typo-primary-base-semibold text-text-header mb-2xs">
                {t('multi.doc-verification.take_a_selfie')}
              </p>

              <p className="typo-primary-base-regular text-text-meta mb-xl">
                {t('multi.doc-verification.take_a_selfie_description')}
              </p>

              {!isMobile ? (
                <Alert
                  alertType={AlertType.Info}
                  showIcon={false}
                  description={t('multi.doc-verification.complete_alert')}
                  className="mb-xl"
                />
              ) : null}

              <div className="flex-1 md:hidden" />

              {submitError ? (
                <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
              ) : null}

              {/* AUTO GENERATED COMPONENTS */}

              <Button
                type="submit"
                className="w-full md:w-min mt-xl"
                isLoading={isSubmitting || isPending}
                onClick={() => void onSubmit()}
                testId="primaryAction_button"
              >
                {isPending
                  ? t('multi.doc-verification.verification_in_progress')
                  : t('multi.doc-verification.start_verification')}
              </Button>
            </>
          ) : null}

          {isShowDocV && incodeSession ? (
            <>
              {
                [
                  !getIncodeClient().isDesktop() && (
                    <ConsentScreen key="consent" session={incodeSession} onAccept={goNext} />
                  ),
                  <MobileRedirect
                    key={`mobile-redirect-${step.toString()}`}
                    session={incodeSession}
                    flowId={verifyId ?? ''}
                    onSuccess={() => {
                      if (getIncodeClient().isDesktop()) {
                        setIsShowDocV(false)
                      } else {
                        // if mobile, skip to selfie directly
                        goNext()
                      }
                    }}
                    onError={handleError}
                  />,
                  <FrontId
                    key={`front-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={goNext}
                    onError={handleError}
                  />,
                  <BackId
                    key={`back-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={goNext}
                    onError={handleError}
                  />,
                  <ProcessId key={`process-${step.toString()}`} session={incodeSession} onSuccess={goNext} />,
                  <Selfie
                    key={`selfie-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={(res) => {
                      setLiveness(res?.liveness)
                      setUserExists(res?.existingUser)
                      goNext()
                    }}
                    onError={handleError}
                  />,
                  <FaceMatch
                    key={`facematch-${step.toString()}`}
                    session={incodeSession}
                    onSuccess={() => {
                      setIsShowDocV(false)
                    }}
                    liveness={liveness}
                    userExists={userExists}
                  />,
                  <RetrySteps
                    key={`retry-${step.toString()}`}
                    session={incodeSession}
                    numberOfTries={3}
                    onSuccess={goNext}
                    onError={handleError}
                  />,
                ].filter(Boolean)[step]
              }
            </>
          ) : null}
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? checkStatusError ?? isIncodeError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
